import { useEffect, useCallback } from 'react';
import { usePathname } from 'next/navigation';

interface PageState {
  scrollPosition: number;
  additionalData?: any;
}

export const usePageState = () => {
  const pathname = usePathname();
  const savePageState = useCallback(() => {
    const state: PageState = {
      // Сохраняем текущую позицию скролла
      scrollPosition: window.scrollY,
      additionalData: {},
    };
    sessionStorage.setItem(`pageState_${pathname}`, JSON.stringify(state));
  }, [pathname]);

  const restorePageState = useCallback(() => {
    const savedState = sessionStorage.getItem(`pageState_${pathname}`);
    if (savedState) {
      const state: PageState = JSON.parse(savedState);
      window.scrollTo({
        top: state.scrollPosition,
        behavior: 'instant',
      });
      return state;
    }
    return null;
  }, [pathname]);

  // Сохраняем позицию скролла при каждом переходе на новую страницу
  // В App Router нет прямого API для событий маршрутизации, поэтому используем
  // эффект размонтирования компонента для сохранения позиции
  useEffect(() => {
    // Сохраняем текущую позицию при размонтировании компонента
    return () => {
      savePageState();
    };
  }, [savePageState]);
  // Также сохраняем позицию при клике на ссылки
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Проверяем, что клик был по ссылке
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      if (link && link.getAttribute('href')) {
        savePageState();
      }
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [savePageState]);

  // Сохраняем позицию при закрытии страницы
  useEffect(() => {
    window.addEventListener('beforeunload', savePageState);
    return () => {
      window.removeEventListener('beforeunload', savePageState);
      savePageState();
    };
  }, [savePageState]);

  // Восстанавливаем позицию при загрузке страницы
  useEffect(() => {
    if (document.readyState === 'complete') {
      restorePageState();
    } else {
      window.addEventListener('load', restorePageState);
      return () => window.removeEventListener('load', restorePageState);
    }
  }, [restorePageState]);

  return {
    savePageState,
    restorePageState,
  };
};
